import React, { useEffect, useState } from 'react';
import {
  AlertTriangle, ExternalLink, Copy, ChevronDown, ChevronRight, CheckCircle, XCircle,
} from 'lucide-react';
import { GlassButton } from '../ui';
import { errText } from './apiErrorText';
import {
  SiteCustomCode, SiteCustomCodeCheck, getSiteCustomCode, saveSiteCustomCode, checkSiteCustomCode,
} from '../../services/api';

interface Props {
  siteId: string;
  onStatus?: (msg: string) => void;
  /** Imported-site editor: there is no "Publishing & Custom Domain" section below the panel. */
  isImported?: boolean;
}

type Busy = '' | 'save' | 'remove' | 'check';

const VENDOR_LABELS: Record<string, string> = {
  google_tag: 'Google tag',
  google_tag_manager: 'Google Tag Manager',
  meta_pixel: 'Meta Pixel',
};

// Client-side constants only — never sent to the server unless the user pastes + saves them.
const EXAMPLES: { id: string; label: string; code: string }[] = [
  {
    id: 'ga4',
    label: 'Google tag (GA4) — Head code',
    code: `<!-- Google tag (gtag.js) -->
<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'G-XXXXXXXXXX');
</script>`,
  },
  {
    id: 'gtm-head',
    label: 'Google Tag Manager — Head code',
    code: `<!-- Google Tag Manager -->
<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-XXXXXXX');</script>
<!-- End Google Tag Manager -->`,
  },
  {
    id: 'gtm-body',
    label: 'Google Tag Manager (noscript) — Body code',
    code: `<!-- Google Tag Manager (noscript) -->
<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-XXXXXXX"
height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>
<!-- End Google Tag Manager (noscript) -->`,
  },
  {
    id: 'meta',
    label: 'Meta Pixel — Head code',
    code: `<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', 'YOUR_PIXEL_ID');
fbq('track', 'PageView');
</script>
<noscript><img height="1" width="1" style="display:none"
src="https://www.facebook.com/tr?id=YOUR_PIXEL_ID&ev=PageView&noscript=1"
/></noscript>
<!-- End Meta Pixel Code -->`,
  },
];

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const textareaStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', fontFamily: MONO, fontSize: 12,
  background: 'var(--panel-strong)', color: 'var(--text)', border: '1px solid var(--stroke)',
  borderRadius: 'var(--radius-sm)', padding: 8, resize: 'vertical',
};

const smallText: React.CSSProperties = { fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.45 };

// Site-level tracking code (Google tag / GTM / Meta Pixel). Save persists immediately and goes live
// on the custom domain on the next page load — there is no Publish step. Never runs in the editor
// or on AiMe preview links (specs/completed/site-builder-custom-code.md).
const SiteCustomCodePanel: React.FC<Props> = ({ siteId, onStatus, isImported }) => {
  const [loaded, setLoaded] = useState<SiteCustomCode | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [head, setHead] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState<Busy>('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const [check, setCheck] = useState<SiteCustomCodeCheck | null>(null);

  const report = (msg: string) => {
    setStatus(msg);
    if (onStatus) onStatus(msg);
  };

  const apply = (r: SiteCustomCode) => {
    setLoaded(r);
    setHead(r.head_code || '');
    setBody(r.body_code || '');
  };

  useEffect(() => {
    let cancelled = false;
    setLoaded(null);
    setLoadFailed(false);
    setStatus('');
    setError('');
    setCheck(null);
    setConfirmRemove(false);
    getSiteCustomCode(siteId)
      .then(r => { if (!cancelled) apply(r); })
      .catch(() => {
        if (!cancelled) {
          setLoadFailed(true);
          setError('Could not load custom code — reload to try again.');
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const canEdit = !!loaded && loaded.can_edit;
  const maxChars = loaded?.max_chars || 20000;
  const dirty = !!loaded && (head !== (loaded.head_code || '') || body !== (loaded.body_code || ''));
  const hasStored = !!loaded && (!!loaded.head_code || !!loaded.body_code);
  // Fail closed: nothing can be changed until the load has succeeded.
  const locked = busy !== '' || loadFailed || !loaded || !canEdit;

  const save = async () => {
    setBusy('save');
    setError('');
    try {
      const r = await saveSiteCustomCode(siteId, head, body);
      apply(r);
      setCheck(null);
      report('Custom code saved — visitors get it on their next page load.'
        + (r.netlify_connected ? ' Your Netlify copy updates on the next deploy (within about 5 minutes).' : ''));
    } catch (err: any) {
      setError(errText(err)); // draft is kept so the user can fix it
    } finally {
      setBusy('');
    }
  };

  const remove = async () => {
    setBusy('remove');
    setError('');
    try {
      const r = await saveSiteCustomCode(siteId, null, null);
      apply(r);
      setConfirmRemove(false);
      setCheck(null);
      report('Custom code removed from every page of this site.'
        + (r.netlify_connected ? ' Your Netlify copy updates on the next deploy (within about 5 minutes).' : ''));
    } catch (err: any) {
      setError(errText(err));
    } finally {
      setBusy('');
    }
  };

  const runCheck = async () => {
    setBusy('check');
    setError('');
    try {
      setCheck(await checkSiteCustomCode(siteId));
    } catch (err: any) {
      setError('Check failed: ' + errText(err));
    } finally {
      setBusy('');
    }
  };

  const copy = (text: string) => {
    try {
      navigator.clipboard?.writeText(text);
      report('Example copied to clipboard.');
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  const counter = (v: string) => (
    <div style={{ ...smallText, textAlign: 'right' }}>
      {v.length.toLocaleString()} / {maxChars.toLocaleString()}
    </div>
  );

  const checkLine = (label: string, found: boolean, expected: boolean) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text)' }}>
      {found ? <CheckCircle size={14} style={{ color: 'var(--success)' }} /> : <XCircle size={14} style={{ color: 'var(--warning)' }} />}
      <span>
        {label} {found ? 'found on your home page' : expected ? 'not found on your home page' : 'not set'}
      </span>
    </div>
  );

  return (
    <div style={{ padding: 16, borderTop: '1px solid var(--stroke)' }}>
      <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
        Custom Code
      </div>
      <div style={{ ...smallText, marginBottom: 10 }}>
        Add tracking code such as a Google tag (GA4), Google Tag Manager or a Meta Pixel. It runs on every page of
        this site — including blog posts and pages AiMe adds — when visitors open it on your custom domain, and in
        exported or Netlify-deployed copies. It never runs in the editor{loaded?.runs_on_aime_origin ? '.' : ' or on AiMe preview links.'}
        {loaded?.runs_on_aime_origin && ' This site is AiMe\u2019s own homepage, so it also runs on the live AiMe homepage and on its Preview tab.'}
      </div>

      <div style={{
        display: 'flex', gap: 6, alignItems: 'flex-start', background: 'var(--color-warning-soft)', color: 'var(--warning)',
        border: '1px solid var(--stroke)', borderRadius: 'var(--radius-sm)', padding: 8, fontSize: 12, lineHeight: 1.45, marginBottom: 12,
      }}>
        <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          This code runs on your live site exactly as entered. Only paste code from providers you trust — a bad snippet
          can break your pages or collect visitor data. You are responsible for any cookie-consent notice the law
          requires for these tools.
        </span>
      </div>

      {loaded && !loaded.can_edit && (
        <div style={{ ...smallText, marginBottom: 10 }}>
          You have view access to this project — ask an editor or the owner to change custom code.
        </div>
      )}

      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }} htmlFor={`cc-head-${siteId}`}>
        Head code
      </label>
      <textarea
        id={`cc-head-${siteId}`}
        rows={8}
        style={textareaStyle}
        value={head}
        maxLength={maxChars}
        spellCheck={false}
        disabled={locked}
        placeholder="<!-- Paste your Google tag or Meta Pixel code here -->"
        onChange={e => setHead(e.target.value)}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <div style={smallText}>
          Added just before &lt;/head&gt; on every page. Paste your Google tag, the first Google Tag Manager snippet,
          or your Meta Pixel base code here.
        </div>
        {counter(head)}
      </div>

      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }} htmlFor={`cc-body-${siteId}`}>
        Body code
      </label>
      <textarea
        id={`cc-body-${siteId}`}
        rows={5}
        style={textareaStyle}
        value={body}
        maxLength={maxChars}
        spellCheck={false}
        disabled={locked}
        onChange={e => setBody(e.target.value)}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <div style={smallText}>
          Added just before &lt;/body&gt; on every page. Paste Google Tag Manager's &lt;noscript&gt; snippet here — it
          works anywhere in the page body — or anything else your provider says belongs in the body.
        </div>
        {counter(body)}
      </div>

      <button
        type="button"
        onClick={() => setShowExamples(v => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12, marginBottom: 8 }}
      >
        {showExamples ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        Show example snippets
      </button>
      {showExamples && (
        <div style={{ marginBottom: 10 }}>
          {EXAMPLES.map(ex => (
            <div key={ex.id} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{ex.label}</span>
                <GlassButton variant="secondary" size="sm" onClick={() => copy(ex.code)}>
                  <Copy size={14} /> Copy
                </GlassButton>
              </div>
              <pre style={{
                margin: 0, fontFamily: MONO, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                background: 'var(--panel-strong)', color: 'var(--text)', border: '1px solid var(--stroke)',
                borderRadius: 'var(--radius-sm)', padding: 8, maxHeight: 180, overflowY: 'auto',
              }}>{ex.code}</pre>
            </div>
          ))}
        </div>
      )}

      {loaded && loaded.detected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {loaded.detected.map(v => (
            <span key={v} style={{
              fontSize: 11, padding: '2px 8px', borderRadius: 9999, background: 'var(--panel-strong)',
              border: '1px solid var(--stroke)', color: 'var(--text)',
            }}>{VENDOR_LABELS[v] || v}</span>
          ))}
        </div>
      )}

      {loaded && loaded.warnings.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {loaded.warnings.map((w, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12, color: 'var(--warning)', marginBottom: 8 }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{error}</span>
        </div>
      )}
      {status && !error && <div style={{ ...smallText, marginBottom: 8 }}>{status}</div>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <GlassButton variant="primary" size="sm" disabled={locked || !dirty} onClick={save}>
          {busy === 'save' ? 'Saving…' : 'Save custom code'}
        </GlassButton>
        <GlassButton variant="secondary" size="sm" disabled={busy !== '' || !dirty} onClick={() => loaded && apply(loaded)}>
          Discard changes
        </GlassButton>
        {hasStored && !confirmRemove && (
          <GlassButton variant="danger" size="sm" disabled={locked} onClick={() => setConfirmRemove(true)}>
            Remove custom code
          </GlassButton>
        )}
      </div>
      {hasStored && confirmRemove && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--text)', marginBottom: 6 }}>
            Remove all custom code from every page of this site?
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <GlassButton variant="danger" size="sm" disabled={locked} onClick={remove}>
              {busy === 'remove' ? 'Removing…' : 'Remove'}
            </GlassButton>
            <GlassButton variant="secondary" size="sm" disabled={busy !== ''} onClick={() => setConfirmRemove(false)}>
              Cancel
            </GlassButton>
          </div>
        </div>
      )}

      {loaded && loaded.updated_at && (
        <div style={{ ...smallText, marginBottom: 10 }}>
          Last changed {new Date(loaded.updated_at).toLocaleString()}
        </div>
      )}

      {loaded && (
        loaded.live_url ? (
          <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <a href={loaded.live_url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text)' }}>
                <ExternalLink size={14} /> Open live site
              </a>
              <GlassButton variant="secondary" size="sm" disabled={busy !== '' || loadFailed} onClick={runCheck}>
                {busy === 'check' ? 'Checking…' : 'Check live page'}
              </GlassButton>
            </div>
            {check && (
              <div style={{ marginBottom: 6 }}>
                {checkLine('Head code', check.head_found, check.head_expected)}
                {checkLine('Body code', check.body_found, check.body_expected)}
              </div>
            )}
            <div style={smallText}>
              To confirm events are reaching Google or Meta, open your live site with Google Tag Assistant or the Meta
              Pixel Helper browser extension.
            </div>
          </div>
        ) : (
          <div style={smallText}>
            Connect a custom domain to run this code on a live site.
            {!isImported ? ' (see Publishing & Custom Domain below)' : ''}
          </div>
        )
      )}
    </div>
  );
};

export default SiteCustomCodePanel;
